import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAppContext } from "../../AppContext";
import { MyDomain } from "../../App";
import "react-phone-input-2/lib/style.css";
import PhoneInput from "react-phone-input-2";
import { useTranslation } from "react-i18next";

function MPHomeLoginWithPhoneNumber({ onLoginSuccess }) {
  const { t } = useTranslation();
  const { setUser, User } = useAppContext();
  const [authStep, setAuthStep] = useState(1);
  const [otp, setOtp] = useState("");
  const [message, setMessage] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [countryCode, setCountryCode] = useState("se");
  const [callingCode, setCallingCode] = useState("+46");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(false);

  // Load localStorage user
  useEffect(() => {
    const saved = localStorage.getItem("user");
    if (saved) {
      const userData = JSON.parse(saved);
      setUser(userData);
      if (onLoginSuccess) {
        onLoginSuccess();
      }
    }
  }, []);

  // Send OTP via Twilio
  const sendOTP = async () => {
    if (!phoneNumber || phoneNumber.length < 7) {
      setMessage(t("PleaseEnterValidPhone"));
      return;
    }

    setMessage("");
    setLoading(true);

    try {
      // Clean phone number - remove leading zero if present
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") {
        cleanedPhone = cleanedPhone.slice(1);
      }

      const fullPhoneNumber = callingCode + cleanedPhone;

      console.log("Sending OTP to:", fullPhoneNumber);

      const response = await axios.post(`${MyDomain}Otp/send`, {
        Phone: fullPhoneNumber,
      });

      if (response.data.success) {
        setMessage(t("OTPSentTo") + " " + fullPhoneNumber);
        setAuthStep(2);
      } else {
        throw new Error(response.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error sending OTP:", error);

      let errorMessage = t("ErrorMessage");
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessage(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP via Twilio
  const verifyOTP = async () => {
    if (!otp || otp.length < 6) {
      setMessage(t("PleaseEnterValidOTP"));
      return;
    }

    setLoading(true);
    try {
      // Clean phone number
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") {
        cleanedPhone = cleanedPhone.slice(1);
      }

      const fullPhoneNumber = callingCode + cleanedPhone;

      console.log("Verifying OTP for:", fullPhoneNumber);

      const response = await axios.post(`${MyDomain}Otp/verify`, {
        Phone: fullPhoneNumber,
        Otp: otp,
      });

      if (response.data.verified) {
        setMessage(t("PhoneVerifiedSuccessfully"));
        setAuthStep(3);
      } else {
        throw new Error("Invalid verification code");
      }
    } catch (err) {
      console.error("Error verifying OTP:", err);
      setMessage(err.response?.data?.message || t("InvalidOTP"));
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const resendOTP = async () => {
    setOtp("");
    setMessage("");
    await sendOTP();
  };

  // Save User to Backend
  const saveUserToBackend = async () => {
    if (!userName.trim()) {
      setMessage(t("PleaseEnterYourName"));
      return;
    }

    setLoading(true);
    try {
      // Clean phone number
      let cleanedPhone = phoneNumber;
      if (cleanedPhone[0] === "0") {
        cleanedPhone = cleanedPhone.slice(1);
      }

      const data = {
        CountryNumber: callingCode,
        PhoneNumber: cleanedPhone.startsWith("0")
          ? cleanedPhone
          : "0" + cleanedPhone,
        Name: userName,
      };

      console.log("=== Saving User to Backend ===");
      console.log("Sending data:", data);

      const response = await axios.post(
        `${MyDomain}Users2/UserLoginWithPhoneNumber`,
        data,
        { headers: { "Content-Type": "application/json" } },
      );

      console.log("Backend response:", response.data);

      localStorage.setItem("user", JSON.stringify(response.data));
      setUser(response.data);

      setMessage(t("LoginSuccessful"));

      if (onLoginSuccess) {
        setTimeout(() => onLoginSuccess(), 500);
      }
    } catch (err) {
      console.error(err);
      setMessage(
        t("FailedToSaveUser") + " " + (err.response?.data || err.message),
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle going back to step 1
  const goBackToPhoneEntry = () => {
    setAuthStep(1);
    setOtp("");
    setMessage("");
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "24px",
        position: "relative",
        width: "100%",
        maxWidth: "500px",
        margin: "0 auto",
      }}
    >
      {/* Progress Indicator */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          padding: "16px 0",
          flexWrap: "wrap",
        }}
      >
        {/* Step 1: Phone */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor:
                authStep === 1
                  ? "#667eea"
                  : authStep > 1
                    ? "#10b981"
                    : "#e5e7eb",
              color: authStep >= 1 ? "#fff" : "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            {authStep > 1 ? "✓" : "1"}
          </div>
          <span
            style={{
              fontSize: "14px",
              fontWeight: authStep === 1 ? "600" : "500",
              color:
                authStep === 1
                  ? "#667eea"
                  : authStep > 1
                    ? "#10b981"
                    : "#9ca3af",
            }}
          >
            {t("Phone")}
          </span>
        </div>

        <div
          style={{
            width: "30px",
            height: "2px",
            backgroundColor: authStep > 1 ? "#10b981" : "#e5e7eb",
          }}
        />

        {/* Step 2: Verify */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor:
                authStep === 2
                  ? "#667eea"
                  : authStep > 2
                    ? "#10b981"
                    : "#e5e7eb",
              color: authStep >= 2 ? "#fff" : "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            {authStep > 2 ? "✓" : "2"}
          </div>
          <span
            style={{
              fontSize: "14px",
              fontWeight: authStep === 2 ? "600" : "500",
              color:
                authStep === 2
                  ? "#667eea"
                  : authStep > 2
                    ? "#10b981"
                    : "#9ca3af",
            }}
          >
            {t("Verify")}
          </span>
        </div>

        <div
          style={{
            width: "30px",
            height: "2px",
            backgroundColor: authStep > 2 ? "#10b981" : "#e5e7eb",
          }}
        />

        {/* Step 3: Name */}
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div
            style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              backgroundColor: authStep === 3 ? "#667eea" : "#e5e7eb",
              color: authStep === 3 ? "#fff" : "#9ca3af",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontWeight: "600",
              fontSize: "14px",
            }}
          >
            3
          </div>
          <span
            style={{
              fontSize: "14px",
              fontWeight: authStep === 3 ? "600" : "500",
              color: authStep === 3 ? "#667eea" : "#9ca3af",
            }}
          >
            {t("Name")}
          </span>
        </div>
      </div>

      {/* STEP 1: Enter Phone Number */}
      {authStep === 1 && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("Welcome")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              {t("EnterPhoneToGetStarted")}
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("PhoneNumber")}
            </label>

            <div
              style={{
                display: "flex",
                gap: "12px",
                alignItems: "stretch",
                marginTop: "8px",
              }}
            >
              <div style={{ minWidth: "110px" }}>
                <PhoneInput
                  country={countryCode}
                  value=""
                  onChange={(value, country) => {
                    setCountryCode(country.countryCode);
                    setCallingCode("+" + country.dialCode);
                  }}
                  enableSearch
                  disableDropdown={false}
                  inputStyle={{ display: "none" }}
                  buttonStyle={{
                    width: "100%",
                    height: "52px",
                    borderRadius: "12px",
                    border: "2px solid #e5e7eb",
                    backgroundColor: "#fff",
                  }}
                  containerStyle={{ width: "100%" }}
                />
                <div
                  style={{
                    marginTop: "6px",
                    textAlign: "center",
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#667eea",
                  }}
                >
                  {callingCode}
                </div>
              </div>

              <input
                type="tel"
                placeholder="50-123-4567"
                value={phoneNumber}
                onChange={(e) =>
                  setPhoneNumber(e.target.value.replace(/[^0-9]/g, ""))
                }
                onKeyPress={(e) => e.key === "Enter" && sendOTP()}
                style={{
                  flex: 1,
                  height: "52px",
                  fontSize: "16px",
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  border: "2px solid #e5e7eb",
                  borderRadius: "12px",
                  outline: "none",
                  transition: "border-color 0.3s ease",
                }}
                onFocus={(e) => (e.target.style.borderColor = "#667eea")}
                onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
              />
            </div>

            <p
              style={{
                marginTop: "12px",
                fontSize: "14px",
                color: "#6b7280",
                padding: "12px",
                backgroundColor: "#f3f4f6",
                borderRadius: "8px",
              }}
            >
              {t("FullNumber")}{" "}
              <strong style={{ color: "#1a1a1a" }}>
                {callingCode + phoneNumber}
              </strong>
            </p>
          </div>

          {message && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                backgroundColor:
                  message.includes(t("ErrorMessage")) ||
                  message.includes(t("InvalidOTP")) ||
                  message.includes("Failed")
                    ? "#fee2e2"
                    : "#d1fae5",
                color:
                  message.includes(t("ErrorMessage")) ||
                  message.includes(t("InvalidOTP")) ||
                  message.includes("Failed")
                    ? "#991b1b"
                    : "#065f46",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {message}
            </div>
          )}

          <button
            onClick={sendOTP}
            disabled={loading || !phoneNumber}
            style={{
              width: "100%",
              padding: "16px",
              fontSize: "16px",
              fontWeight: "600",
              border: "none",
              borderRadius: "12px",
              backgroundColor: loading || !phoneNumber ? "#d1d5db" : "#667eea",
              color: "#fff",
              cursor: loading || !phoneNumber ? "not-allowed" : "pointer",
              boxShadow:
                loading || !phoneNumber
                  ? "none"
                  : "0 4px 12px rgba(102, 126, 234, 0.3)",
              transition: "all 0.3s ease",
              outline: "none",
            }}
          >
            {loading ? t("Sending") : t("SendVerificationCode")}
          </button>
        </div>
      )}

      {/* STEP 2: Verify OTP */}
      {authStep === 2 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("VerifyYourNumber")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              {t("WeSentCodeTo")} <strong>{callingCode + phoneNumber}</strong>
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("VerificationCode")}
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder={t("EnterSixDigitCode")}
              value={otp}
              onChange={(e) =>
                setOtp(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
              }
              onKeyPress={(e) => e.key === "Enter" && verifyOTP()}
              maxLength={6}
              style={{
                width: "100%",
                height: "52px",
                fontSize: "20px",
                textAlign: "center",
                letterSpacing: "8px",
                paddingLeft: "16px",
                paddingRight: "16px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                outline: "none",
                transition: "border-color 0.3s ease",
                fontWeight: "600",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          {message && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                backgroundColor:
                  message.includes(t("InvalidOTP")) ||
                  message.includes(t("ErrorMessage")) ||
                  message.includes("Invalid")
                    ? "#fee2e2"
                    : "#d1fae5",
                color:
                  message.includes(t("InvalidOTP")) ||
                  message.includes(t("ErrorMessage")) ||
                  message.includes("Invalid")
                    ? "#991b1b"
                    : "#065f46",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {message}
            </div>
          )}

          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            <button
              onClick={verifyOTP}
              disabled={loading || otp.length < 6}
              style={{
                width: "100%",
                padding: "16px",
                fontSize: "16px",
                fontWeight: "600",
                border: "none",
                borderRadius: "12px",
                backgroundColor:
                  loading || otp.length < 6 ? "#d1d5db" : "#667eea",
                color: "#fff",
                cursor: loading || otp.length < 6 ? "not-allowed" : "pointer",
                boxShadow:
                  loading || otp.length < 6
                    ? "none"
                    : "0 4px 12px rgba(102, 126, 234, 0.3)",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            >
              {loading ? t("Verifying") : t("VerifyCode")}
            </button>

            <button
              onClick={resendOTP}
              disabled={loading}
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "14px",
                fontWeight: "600",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                backgroundColor: "#fff",
                color: "#667eea",
                cursor: loading ? "not-allowed" : "pointer",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            >
              {loading ? t("Sending") : t("ResendCode") || "Resend Code"}
            </button>

            <button
              onClick={goBackToPhoneEntry}
              style={{
                width: "100%",
                padding: "14px",
                fontSize: "14px",
                fontWeight: "600",
                border: "1px solid #e5e7eb",
                borderRadius: "12px",
                backgroundColor: "#fff",
                color: "#6b7280",
                cursor: "pointer",
                transition: "all 0.3s ease",
                outline: "none",
              }}
            >
              {t("ChangePhoneNumber")}
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: Enter Name */}
      {authStep === 3 && (
        <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <div>
            <h3
              style={{
                fontSize: "24px",
                fontWeight: "700",
                color: "#1a1a1a",
                marginBottom: "8px",
                textAlign: "center",
              }}
            >
              {t("AlmostThere")}
            </h3>
            <p
              style={{
                fontSize: "14px",
                color: "#6b7280",
                textAlign: "center",
                marginBottom: "24px",
              }}
            >
              {t("EnterNameToComplete")}
            </p>
          </div>

          <div>
            <label
              style={{
                fontWeight: "600",
                fontSize: "14px",
                color: "#374151",
                marginBottom: "8px",
                display: "block",
              }}
            >
              {t("YourName")}
            </label>
            <input
              type="text"
              placeholder={t("EnterFullName")}
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              onKeyPress={(e) => e.key === "Enter" && saveUserToBackend()}
              style={{
                width: "100%",
                height: "52px",
                fontSize: "16px",
                paddingLeft: "16px",
                paddingRight: "16px",
                border: "2px solid #e5e7eb",
                borderRadius: "12px",
                outline: "none",
                transition: "border-color 0.3s ease",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#667eea")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          {message && (
            <div
              style={{
                padding: "12px 16px",
                borderRadius: "8px",
                backgroundColor:
                  message.includes(t("FailedToSaveUser")) ||
                  message.includes(t("ErrorMessage"))
                    ? "#fee2e2"
                    : "#d1fae5",
                color:
                  message.includes(t("FailedToSaveUser")) ||
                  message.includes(t("ErrorMessage"))
                    ? "#991b1b"
                    : "#065f46",
                fontSize: "14px",
                textAlign: "center",
              }}
            >
              {message}
            </div>
          )}

          <button
            onClick={saveUserToBackend}
            disabled={loading || !userName.trim()}
            style={{
              width: "100%",
              padding: "16px",
              fontSize: "16px",
              fontWeight: "600",
              border: "none",
              borderRadius: "12px",
              backgroundColor:
                loading || !userName.trim() ? "#d1d5db" : "#667eea",
              color: "#fff",
              cursor: loading || !userName.trim() ? "not-allowed" : "pointer",
              boxShadow:
                loading || !userName.trim()
                  ? "none"
                  : "0 4px 12px rgba(102, 126, 234, 0.3)",
              transition: "all 0.3s ease",
              outline: "none",
            }}
          >
            {loading ? t("Completing") : t("CompleteRegistration")}
          </button>
        </div>
      )}
    </div>
  );
}

export default MPHomeLoginWithPhoneNumber;
